import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { concatMap, delay, EMPTY, exhaustMap, Observable, of, switchMap } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { assetsActions, assetsApiActions, assetsTreeActions } from 'app/actions/assets';
import { AssetsService } from 'app/services/assets.service';
import { MachinesService } from 'app/services/machines.service';
import { ParamsUtils } from 'app/utils/api/params.utils';
import * as fromAssets from 'app/reducers/assets';
import * as fromRouter from 'app/reducers/router';
import {
  AssetTreeDto,
  AssetTreeStorage,
  AssetTreeVM,
  CreateAssetBody,
  UpdateAssetBody
} from 'app/models/api/asset-tree';
import { AssetsUtils } from 'app/utils/reducers/assets.utils';
import { AssetHierarchyUtils } from 'app/utils/api/asset-hierarchy.utils';
import { AssetStateEnum } from 'app/models/enums/asset';
import { VirtualPaginationOptions } from 'app/models/pagination-options';
import { MachinesFilterOptions } from 'app/models/api/machines';
import { OrderForRequestBody } from 'app/models/order';
import { concatLatestFrom } from '@ngrx/operators';
import { ApplicationStore } from '../state-management/application/application.store';

@Injectable()
export class AssetsEffects {
  private actions$: Actions = inject(Actions);
  private store: Store = inject(Store);
  private router: Router = inject(Router);
  private assetsService: AssetsService = inject(AssetsService);
  private machinesService: MachinesService = inject(MachinesService);
  private appStore: ApplicationStore = inject(ApplicationStore);

  initAssetTree$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsActions.initAssetTree),
    switchMap(() => of(this.appStore.assetsData()).pipe(
      map((data: AssetTreeStorage | null) => !!data
        ? assetsActions.getAssetTreeStateFromStorage({ data })
        : assetsActions.checkForSelectedNodeInUrl()
      )
    )),
  ));

  loadMachinesInModalCases$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(
      assetsActions.openAssetTreeModal,
      assetsActions.loadAdditionalMachinesOnScroll,
      assetsActions.searchMachines,
      assetsActions.changeOrder,
    ),
    concatLatestFrom(() => [
      this.store.select(fromAssets.getMachinesPagination),
      this.store.select(fromAssets.getIsAllMachinesLoaded),
    ]),
    // count shouldn't be equal 0 to start load machines for modal
    switchMap(([, { count }, isAllMachinesLoaded]: [Action, VirtualPaginationOptions, boolean]) =>
      count === 0 || isAllMachinesLoaded
        ? EMPTY
        : of(assetsApiActions.loadMachinesForModal()
     )),
  ));

  checkForSelectedNodeInUrl$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsActions.checkForSelectedNodeInUrl),
    concatLatestFrom(() => this.store.select(fromRouter.getAssetNodeId)),
    map(([, nodeId]: [Action, string|null]) => nodeId
      ? assetsApiActions.loadAssetTreeInitialForSelected({ nodeId })
      : assetsApiActions.loadAssetTreeInitial({ params: { depthLevel: 1 } })
    ),
  ));

  loadAssetTreeInitialForSelectedSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.loadAssetTreeInitialForSelectedSuccess),
    map(({ nodeId }) => assetsTreeActions.openTargetElementOnTree({ nodeId })),
  ));

  loadAssetTreeInitialSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.loadAssetTreeInitialSuccess),
    map(() => assetsTreeActions.calculateExpandCollapseNodes()),
  ));

  loadAssetTreePartSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.loadAssetTreePartSuccess),
    map(() => assetsTreeActions.calculateExpandCollapseNodes()),
  ));

  loadAssetTreePartFail$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.loadAssetTreePartFail),
    map(() => assetsTreeActions.calculateExpandCollapseNodes()),
  ));

  openTargetElementOnTree$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsTreeActions.openTargetElementOnTree),
    map(() => assetsTreeActions.calculateExpandCollapseNodes()),
  ));

  removedIssuesFromStorageLoadedData$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsActions.removedIssuesFromStorageLoadedData),
    map(({ nodeId }) => nodeId
      ? assetsTreeActions.openTargetElementOnTree({ nodeId }) // open target element on tree after processing data from storage
      : assetsTreeActions.calculateExpandCollapseNodes(), // just make all needed calculation if target is not selected
    ),
  ));

  expandCollapseNode$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsTreeActions.expandCollapseNode),
    mergeMap(({ nodeId }) => [
      assetsTreeActions.calculateExpandCollapseNodes(),
      assetsActions.checkForLoadedChildren({ nodeId }),
    ]),
  ));

  checkForLoadedChildren$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsActions.checkForLoadedChildren),
    map(action => action.nodeId),
    concatLatestFrom(() => [
      this.store.select(fromAssets.selectAssetTreeView),
      this.store.select(fromAssets.selectNodesInLoading),
    ]),
    switchMap(([nodeId, tree, inLoading]: [string, AssetTreeVM[], string[]]) => AssetsUtils.isChildrenOfNodeAreNotLoaded(tree, nodeId, inLoading)
      ? of(assetsApiActions.loadAssetTreePart({ params: { nodeId, depthLevel: 1 } }))
      : EMPTY
    ),
  ));

  reloadFailedNode$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsActions.reloadFailedNode),
    map(({ nodeId }) => assetsApiActions.loadAssetTreePart({ params: { nodeId, depthLevel: 1 } })),
  ));

  openSearchedElementOnTree$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsTreeActions.openSearchedElementOnTree),
    map(({ node }) => node),
    map(({ nodeId }) => assetsActions.checkForExistingElementInAssetTree({ nodeId })),
  ));

  checkForExistingInAssetTree$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsActions.checkForExistingElementInAssetTree),
    concatLatestFrom(() => [
      this.store.select(fromAssets.selectAssetTreeView),
      this.store.select(fromAssets.selectRootNodeId),
    ]),
    map(([{ nodeId }, tree, rootNodeId]: [{ nodeId: string }, AssetTreeVM[], string|null]) =>
      AssetsUtils.checkIsTargetIdExistInTree(tree, nodeId, rootNodeId)
        ? assetsActions.removedIssuesFromStorageLoadedData({ nodeId }) // removed issue then pass target id
        : assetsApiActions.loadAssetTreeInitialForSelected({ nodeId }),
    ),
  ));

  // recalculate tree after each tree CrUD operations
  crudAssetGroupSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(
      assetsApiActions.createAssetGroupSuccess,
      assetsApiActions.addNewMachinesToAssetTreeSuccess,
      assetsApiActions.deleteAssetSuccess,
      assetsApiActions.renameAssetGroupSuccess,
      assetsTreeActions.renameNodeIdAfterMachineRenaming,
      assetsTreeActions.enableMachineNodeIfDeletingOfMachineWasFailed,
      assetsTreeActions.disableMachineNodeWhileMachineIsDeleting,
      assetsTreeActions.removeMachineNodeAfterMachineWasDeleted,
    ),
    map(() => assetsTreeActions.calculateExpandCollapseNodes()),
  ));

  // check if main view should be reloaded, all actions should have response with Dto
  reloadMainViewCheckCases$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(
      assetsApiActions.addNewMachinesToAssetTreeSuccess,
      assetsApiActions.deleteAssetSuccess,
    ),
    map(action => action?.parentNodeId ?? null),
    concatLatestFrom(() => this.store.select(fromRouter.getAssetNodeId)),
    switchMap(([parentNodeId, currentTargetId]: [string|null, string|null]) => parentNodeId === currentTargetId
      ? of(assetsActions.reloadMainViewForCurrentBranch())
      : EMPTY
    ),
    // TODO temporary here, backend needs a little time to be updated, after backend is going to be fixed, this line could be removed
    delay(500),
  ));

  // this effect is responsible for saving asset tree into session storage, please uncomment it, if we need this functionality back
  // calculateExpandCollapseNodes$: Observable<Action> = createEffect(() => this.actions$.pipe(
  //   ofType(assetsTreeActions.calculateExpandCollapseNodes),
  //   concatLatestFrom(() => [
  //     this.store.select(fromAssets.selectAssetTreeView),
  //     this.store.select(fromAssets.selectRootNodeId),
  //     this.store.select(fromAssets.selectSelected),
  //   ]),
  //   map(([, assetTreeView, rootNodeId, selected]: [Action, AssetTreeVM[], string|null, AssetSelected]) =>
  //     assetsActions.saveAssetTreeStateInStorage({ data: { assetTreeView, rootNodeId, selected } })),
  // ));

  /* STORAGE BLOCK */
  getAssetTreeStateFromStorage$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsActions.getAssetTreeStateFromStorage),
    concatLatestFrom(() => this.store.select(fromRouter.getAssetNodeId)),
    map(([, nodeId]: [Action, string | null]) => nodeId
      ? assetsActions.checkForExistingElementInAssetTree({ nodeId })
      : assetsActions.removedIssuesFromStorageLoadedData({ nodeId }) // removed issue then pass null
    ),
  ));

  saveAssetTreeStateInStorage$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsActions.saveAssetTreeStateInStorage),
    tap(({ data }) => this.appStore.setAssetsData(data)),
  ), { dispatch: false });

  /* API BLOCK */
  loadAssetTreeInitial$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.loadAssetTreeInitial),
    map(action => ParamsUtils.convertObjectToHttpParams(action.params)),
    switchMap((httpParams: HttpParams|null) => this.assetsService.getAssetTree(httpParams).pipe(
      map(response => assetsApiActions.loadAssetTreeInitialSuccess({ response })),
      catchError(error => of(assetsApiActions.loadAssetTreeInitialFail({ error }))),
    )),
  ));

  loadAssetTreeInitialForSelected$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.loadAssetTreeInitialForSelected),
    exhaustMap(({ nodeId }) => this.assetsService.getAssetTreeForTargetElement(nodeId).pipe(
      map((response: AssetTreeDto[]) => assetsApiActions.loadAssetTreeInitialForSelectedSuccess({ response, nodeId })),
      catchError((error) => of(assetsApiActions.loadAssetTreeInitialForSelectedFail({ error }))),
    )),
  ));

  provideChangesInRouteAfterLoadingCases$ = createEffect(() => this.actions$.pipe(
    ofType(
      assetsApiActions.loadAssetTreeInitialFail,
      assetsApiActions.loadAssetTreeInitialForSelectedFail,
    ),
    concatLatestFrom(() => [
      this.store.select(fromRouter.getIsAssetOpen),
      this.store.select(fromAssets.selectRootNodeId),
      this.store.select(fromRouter.getMachineId),
    ]),
    tap(([, isAssetOpen, rootNodeId, machineId]: [Action, boolean, string|null, string|null]) => {
      const { commands, extras } = AssetsUtils.getNavigation(null, isAssetOpen ? AssetStateEnum.OPEN : AssetStateEnum.CLOSE, rootNodeId, machineId);
      this.router.navigate(commands, extras);  // go to main if we hit an error, also just in case add root group id for success if it is not in route
    }),
  ), { dispatch: false });

  loadAssetTreePart$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.loadAssetTreePart),
    map(action => [ParamsUtils.convertObjectToHttpParams(action.params), action.params?.nodeId]),
    mergeMap(([httpParams, nodeId]: [HttpParams, string|undefined]) => this.assetsService.getAssetTree(httpParams).pipe(
      map(response => assetsApiActions.loadAssetTreePartSuccess({ response, ...(nodeId && { nodeId }) })),
      catchError(error => of(assetsApiActions.loadAssetTreePartFail({ error, ...(nodeId && { nodeId }) }))),
    )),
  ));

  createAssetGroup$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.createAssetGroup),
    map(action => AssetHierarchyUtils.createAssetGroupCreateBody({
      nodeDescription: action.groupName,
      parentNodeId: action.parentNodeId,
    })),
    switchMap((body: CreateAssetBody) => this.assetsService.createUpdateAssetNode(body).pipe(
      map(response => assetsApiActions.createAssetGroupSuccess({ response })),
      catchError(error => of(assetsApiActions.createAssetGroupFail({ error }))),
    ))
  ));

  renameAssetGroup$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.renameAssetGroup),
    map(action => AssetHierarchyUtils.createAssetGroupUpdateBody({
      nodeDescription: action.newName,
      nodeId: action.nodeId,
    })),
    switchMap((body: UpdateAssetBody) => this.assetsService.createUpdateAssetNode(body).pipe(
      map(response => assetsApiActions.renameAssetGroupSuccess({ response })),
      catchError(error => of(assetsApiActions.renameAssetGroupFail({ error }))),
    ))
  ));

  deleteAsset$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.deleteAsset),
    map(({ nodeId, parentNodeId }) => ({ nodeId, parentNodeId })),
    switchMap(({ nodeId, parentNodeId }) => this.assetsService.deleteAssetNode(nodeId).pipe(
      map((response: AssetTreeDto[]) => assetsApiActions.deleteAssetSuccess({ response, parentNodeId })),
      catchError(error => of(assetsApiActions.deleteAssetFail({ error }))),
    )),
  ));

  addNewMachinesToAssetTree$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.addNewMachinesToAssetTree),
    map(action => [
      AssetHierarchyUtils.createAssetMachinesCreateBody({ assetsIds: action.assetsIds, parentNodeId: action.parentNodeId }),
      action.parentNodeId
    ]),
    switchMap(([body, parentNodeId]: [CreateAssetBody, string | null]) => this.assetsService.createUpdateAssetNode(body).pipe(
      map(response => assetsApiActions.addNewMachinesToAssetTreeSuccess({ response, parentNodeId })),
      catchError(error => of(assetsApiActions.addNewMachinesToAssetTreeFail({ error }))),
    ))
  ));

  loadMachinesForModal$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(assetsApiActions.loadMachinesForModal),
    concatLatestFrom(() => [
      this.store.select(fromAssets.getMachinesPagination),
      this.store.select(fromAssets.getSearchTerm),
      this.store.select(fromAssets.getOrderDecomposed),
    ]),
    map(([, { count, offset }, filter, { orderBy, orderDirection }]: [Action, VirtualPaginationOptions, string|null, OrderForRequestBody]) =>
      ({ count, offset, filter, orderBy, orderDirection, nodeFilter: { nodeId: null } }),
    ),
    concatMap((body: MachinesFilterOptions) => this.machinesService.getMachinesOverview(body).pipe(
      map(response => assetsApiActions.loadMachinesForModalSuccess({ response })),
      catchError(error => of(assetsApiActions.loadMachinesForModalFail({ error }))),
    )),
  ));
}
